import React, { useEffect, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

import { SidebarItemChildrenType } from 'src/interfaces/idashboard';
import { SidebarItemProps } from 'src/interfaces/iproptypes';

import SidebarOption from './SidebarOption';
import { sampleMarket } from 'src/utils/const';
import { useHistory } from 'react-router-dom';
import { openSidebarRedirectionLink } from './sidebarLinkUtils';
import postOnSlack from 'src/core/postOnSlack';

/** Light panel — dark teal was hiding body text */
const FLYOUT_BG = 'linear-gradient(180deg, #ffffff 0%, #f3fafb 100%)';

const SidebarItem = ({
  item,
  isSubGroup,
  handleSidebarClick,
  handleSidebarListItemClick,
  matches,
  sidebarOpen,
  setSidebarOpen,
  index,
  selectedSidebarIndex,
  user,
  setOpenReportComparePage,
}: SidebarItemProps) => {
  const history = useHistory();
  const hasSubOptions = item.children ? item.children.length > 0 : false;

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  let isOptionGroupSelected: boolean = false;

  useEffect(() => {
    !sidebarOpen && setIsExpanded(false);
  }, [sidebarOpen]);

  if (hasSubOptions) {
    (item.children as SidebarItemChildrenType[]).forEach(data => {
      if (data.listIndex === selectedSidebarIndex) {
        isOptionGroupSelected = true;
      }
    });
  }

  const sendSlackNotification = (clickedItem: string) => {
    postOnSlack(
      'C05JDGPMH8T',
      `User: ${user.email} clicked on ${clickedItem} Button In the App SideBar`
    );
  };

  const handleChildNavigate = (element: SidebarItemChildrenType) => {
    if (
      element.name === 'Custom Data' ||
      element.name === 'API' ||
      element.name === 'Property-level Performance Data' ||
      element.name === 'Hotel Data' ||
      element.name === 'Enterprise'
    ) {
      sendSlackNotification(element.name);
    }

    setOpenReportComparePage && setOpenReportComparePage(false);

    if (element.redirectionLink) {
      openSidebarRedirectionLink(element.redirectionLink, history);
      setIsExpanded(false);
      return;
    }

    if (
      (window.location.pathname == '/airbnb-data' ||
        window.location.pathname == '/airbnb-data/' ||
        window.location.pathname == '/') &&
      element.listIndex === 4
    ) {
      setIsExpanded(false);
      window.open(`/airbnb-data/united-kingdom/0/${sampleMarket}?guests=true`, '_self');
      return;
    }

    handleSidebarListItemClick(element.listIndex);
    handleSidebarClick && handleSidebarClick(element.stateName);
    setIsExpanded(false);
  };

  /** Desktop: parent rows with nested links — floating popover instead of inline tree */
  if (hasSubOptions) {
    return (
      <>
        <div
          style={{
            backgroundColor: 'transparent',
            margin: '10px',
            borderRadius: 10,
            overflow: 'visible',
            marginBottom: '2.5px',
          }}
        >
          <Box ref={anchorRef}>
            <SidebarOption
              item={item}
              isSubGroup={isSubGroup}
              handleSidebarClick={handleSidebarClick}
              handleSidebarListItemClick={handleSidebarListItemClick}
              matches={matches}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              index={index}
              selectedSidebarIndex={selectedSidebarIndex}
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
              user={user}
              setOpenReportComparePage={setOpenReportComparePage}
              isFlyoutParent
              isOptionGroupSelected={isOptionGroupSelected}
            />
          </Box>
        </div>

        <Popover
          open={isExpanded}
          anchorEl={anchorRef.current}
          onClose={() => setIsExpanded(false)}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          PaperProps={{
            elevation: 12,
            sx: {
              ml: 1,
              borderRadius: '14px',
              overflow: 'hidden',
              background: FLYOUT_BG,
              border: '1px solid rgba(3, 122, 144, 0.22)',
              boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
              minWidth: 268,
              zIndex: 1400,
            },
          }}
          disableRestoreFocus
        >
          <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
            <Box
              sx={{
                border: '1px solid rgba(3, 122, 144, 0.35)',
                borderRadius: '8px',
                px: 1.25,
                py: 1,
                background: 'rgba(255, 255, 255, 0.95)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#0f172a',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.02em',
                  lineHeight: 1.35,
                }}
              >
                {item.name}
              </Typography>
            </Box>
          </Box>

          <Stack spacing={0} sx={{ px: 2, pb: 2 }}>
            {(item.children as SidebarItemChildrenType[]).map(element => (
              <Box
                key={element.listIndex}
                onClick={() => handleChildNavigate(element)}
                sx={{
                  py: 1.1,
                  px: 1.25,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor:
                    element.listIndex === selectedSidebarIndex
                      ? 'rgba(3, 122, 144, 0.12)'
                      : 'transparent',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(3, 122, 144, 0.08)',
                  },
                }}
              >
                <Typography
                  sx={{
                    color: '#0f172a',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.01em',
                  }}
                >
                  {element.name}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Popover>
      </>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'transparent',
        margin: '10px',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: '2.5px',
      }}
    >
      <SidebarOption
        item={item}
        isSubGroup={isSubGroup}
        handleSidebarClick={handleSidebarClick}
        handleSidebarListItemClick={handleSidebarListItemClick}
        matches={matches}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        index={index}
        selectedSidebarIndex={selectedSidebarIndex}
        user={user}
        setOpenReportComparePage={setOpenReportComparePage}
      />
    </div>
  );
};

export default SidebarItem;
